<template>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="computedWidth"
    content-class="dealer-cc-dialog"
    overlay-opacity="0.75"
    :fullscreen="isMobile"
  >
    <div v-if="isDealerAdmin && dialog">
      <v-card flat>
        <v-card-title>
          <v-list-item three-line>
            <v-list-item-content>
              <v-list-item-title
                class="text-h5 mb-1 mr-2 text-wrap"
                style="word-break: break-word"
              >
                <v-icon> $creditCardIcon </v-icon>
                {{ titleText }}
              </v-list-item-title>
              <v-list-item-subtitle class="pr-1 pb-1">{{
                subTitleText
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-container fluid>
            <v-row class="pa-0">
              <v-col cols="12" class="pa-0">
                <v-radio-group
                  hide-details
                  v-model="radios"
                  @change="changePayment"
                >
                  <v-expansion-panels
                    mandatory
                    :accordian="true"
                    flat
                    readonly
                    :value="Number(radios)"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header hide-actions>
                        <v-radio value="0">
                          <template v-slot:label>
                            {{ $t('dealer.chargify.creditCard') }}
                          </template>
                        </v-radio>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <CCEntryForm
                          ref="CCEntryFormRef"
                          :showCCButtons="false"
                          :isDealerAdmin="isDealerAdmin"
                          @decline="declineAndLogout"
                          @closeDialog="closeDialog"
                          :localLogoutLoading="localLogoutLoading"
                          :fullscreenPromptView="true"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel>
                      <v-expansion-panel-header hide-actions>
                        <v-radio value="1">
                          <template v-slot:label>
                            {{ $t('dealer.chargify.automatedClearingHouse') }}
                          </template>
                        </v-radio>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <ACHEntryForm
                          ref="ACHEntryFormRef"
                          @decline="declineAndLogout"
                          @closeDialog="closeDialog"
                          :showACHButtons="false"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="12">
              <div class="d-flex justify-end">
                <v-btn
                  v-if="isUpdatePaymentMethod"
                  @click="cancel"
                  class="mr-2"
                  :loading="localLogoutLoading"
                  :disabled="addPaymentLoading"
                >
                  <template>
                    {{ $t('global.cancel') }}
                  </template>
                </v-btn>
                <v-btn
                  v-else
                  @click="declineAndLogout"
                  class="mr-2"
                  :loading="localLogoutLoading"
                  :disabled="addPaymentLoading"
                >
                  <template>
                    {{ $t('dealer.chargify.declineAndLogout') }}
                  </template>
                </v-btn>
                <v-btn
                  @click="addPaymentClick"
                  color="secondary"
                  :disabled="addPaymentDisabled"
                  :loading="addPaymentLoading"
                >
                  {{ $t('dealer.chargify.addPaymentMethod') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </div>
    <DealerTechCC
      v-if="!isDealerAdmin && dialog"
      :localLogoutLoading="localLogoutLoading"
      @decline="declineAndLogout"
    />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { isMobile } from 'mobile-device-detect'
import { mapActions, mapGetters, mapState } from 'vuex'
import CCEntryForm from '../CCEntryForm.vue'
import DealerTechCC from './DealerTechCC.vue'
import ACHEntryForm from '../ACHEntryForm.vue'

export default defineComponent({
  name: 'DealerCCRequiredDialog',
  components: {
    CCEntryForm,
    DealerTechCC,
    ACHEntryForm,
  },
  data() {
    return {
      dialog: false,
      localLogoutLoading: false,
      radios: '0',
      paymentLoading: false,
    }
  },
  computed: {
    ...mapGetters('Security', [
      'dealerRequiresCC',
      'hasBankAccount',
      'hasAcceptedAllTerms',
      'isDealerUser',
      'hasSeenDealerOnboarding',
      'isDealerAdmin',
      'dealerIsBillable',
    ]),
    ...mapGetters('Environment', ['hasChargify']),
    ...mapState('Security', ['isLoggedIn', 'user']),
    ...mapState('Payment', [
      'addPaymentLoading',
      'achAuthorized',
      'isUpdatePaymentMethod',
    ]),
    isMobile() {
      return isMobile
    },
    titleText() {
      if (this.isUpdatePaymentMethod) {
        return this.$t('dealer.chargify.addPaymentToContinue')
      } else {
        return this.$t('dealer.chargify.addPaymentToContinueWelcome', [
          this.user.firstName,
        ])
      }
    },
    subTitleText() {
      if (this.user.isCardExpired) {
        return this.$t('dealer.chargify.paymentMethodIssue')
      } else {
        return this.$t('dealer.chargify.addPaymentToAccessVigil')
      }
    },
    showDealerCCPrompt() {
      return (
        this.hasChargify &&
        this.isLoggedIn &&
        this.isDealerUser &&
        this.dealerRequiresCC &&
        !this.hasBankAccount &&
        this.hasAcceptedAllTerms &&
        // removing for now, as new dealers will not see the onboarding until they navigate to /customers
        //this.hasSeenDealerOnboarding &&
        this.dealerIsBillable
      )
    },
    computedWidth() {
      if (this.isDealerAdmin) {
        return '600'
      } else {
        return '500'
      }
    },
    addPaymentDisabled() {
      return (
        this.localLogoutLoading || (!this.achAuthorized && this.radios == '1')
      )
    },
  },
  methods: {
    ...mapActions('Security', ['logout']),
    ...mapActions('Payment', ['callUpdatePaymentMethod', 'goToPaymentTab']),
    showDialog() {
      if (this.showDealerCCPrompt) {
        this.dialog = true
      } else {
        this.dialog = false
      }
    },
    closeDialog() {
      this.radios = '0'
      this.dialog = false
      this.callUpdatePaymentMethod(false)
      if (this.$route.name === 'Administration') {
        this.goToPaymentTab(true)
      } else {
        this.$router.push({ name: 'Administration', params: { tab: 3 } })
      }
    },
    changePayment() {
      if (this.radios == '0') {
        this.$refs.CCEntryFormRef?.reloadCard()
      } else if (this.radios == '1') {
        this.$refs.ACHEntryFormRef?.reloadCard()
      }
    },
    addPaymentClick() {
      // leaving for now, might need this
      //this.callUpdatePaymentMethod(false)
      if (this.radios == '0') {
        this.$refs.CCEntryFormRef.addPaymentClick()
      } else {
        this.$refs.ACHEntryFormRef.addPaymentClick()
      }
    },
    async declineAndLogout() {
      this.localLogoutLoading = true
      await this.logout()
      this.closeDialog()
      this.localLogoutLoading = false
      this.$router.push('/logout')
    },
    cancel() {
      this.callUpdatePaymentMethod(false)
      this.closeDialog()
    },
  },
  mounted() {
    this.showDialog()
  },
  watch: {
    user() {
      this.showDialog()
    },
    hasSeenDealerOnboarding() {
      this.showDialog()
    },
    showDealerCCPrompt() {
      this.showDialog()
    },
    isUpdatePaymentMethod() {
      if (this.isUpdatePaymentMethod) {
        this.dialog = true
      }
    },
  },
})
</script>

<style lang="scss">
.dealer-cc-dialog {
  max-height: 100% !important;
  .v-input--selection-controls {
    margin-top: 0;
  }
  .v-expansion-panel {
    margin-top: 0;
    .v-expansion-panel-content__wrap {
      padding: 0 8px;
    }
  }
}
</style>
